/**
 * Project Members & Responsibilities Utilities
 */

/**
 * Extract an array of responsibilities from a project member object
 * @param {object} member
 * @returns {string[]}
 */
export function getMemberResponsibilities(member) {
  if (!member) return [];

  const raw =
    member.responsibilities ??
    member.responsibility ??
    member.user?.responsibilities ??
    member.user?.responsibility ??
    member.userId?.responsibilities ??
    member.userId?.responsibility ??
    [];

  if (Array.isArray(raw)) {
    return raw
      .map((r) => (typeof r === "string" ? r.trim() : String(r || "")))
      .filter(Boolean);
  }

  if (typeof raw === "string" && raw.trim()) {
    return [raw.trim()];
  }

  return [];
}

/**
 * Check if a project member has a specific responsibility
 * @param {object} member
 * @param {string} targetResponsibility
 * @returns {boolean}
 */
export function memberHasResponsibility(member, targetResponsibility) {
  if (!member || !targetResponsibility) return false;
  const target = targetResponsibility.toLowerCase().trim();
  const responsibilities = getMemberResponsibilities(member);
  return responsibilities.some((r) => r.toLowerCase().trim() === target);
}

/**
 * Filter project members that have a given responsibility
 * @param {Array} members
 * @param {string} targetResponsibility
 * @returns {Array}
 */
export function filterMembersByResponsibility(members = [], targetResponsibility) {
  if (!Array.isArray(members) || !targetResponsibility) return [];
  return members.filter((member) => memberHasResponsibility(member, targetResponsibility));
}

/**
 * Extract user ID from a member object
 * @param {object} member
 * @returns {string|null}
 */
export function getMemberUserId(member) {
  if (!member) return null;
  if (typeof member.user === "object" && member.user?._id) return member.user._id;
  if (typeof member.user === "string") return member.user;
  if (typeof member.userId === "object" && member.userId?._id) return member.userId._id;
  if (typeof member.userId === "string") return member.userId;
  return member._id || member.id || null;
}

/**
 * Extract display name from a member object
 * @param {object} member
 * @returns {string}
 */
export function getMemberDisplayName(member) {
  if (!member) return "Unknown";
  return (
    member.user?.name ||
    member.user?.username ||
    member.name ||
    member.username ||
    member.userId?.name ||
    member.userId?.username ||
    member.user?.email ||
    member.email ||
    "Unknown"
  );
}
